import { formatQuantityWithMeasurement, formatShortDate } from "../../../../Utils/Utils";
import useApi from "../../../../Utils/BackendClient";
import useGetEnums from "../../../../Utils/EnumsUtils";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { useIsMobile } from "../../../../Utils/ResizeUtils";

import CommonToolbar from "../../../UIElements/CommonToolbar";
import DocumentArchiveTable from "../../../UIElements/DocumentArchiveTable";
import EnumsContext from "../../../../context/enums-context";
import HistoryElement from "../../../UIElements/HistoryElement";

import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { TabMenu } from "primereact/tabmenu";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";

import commonStyle from "../../CommonStyles.module.css";
import DropdownInput from "../../../UIElements/Inputs/DropdownInput";
import CalendarInput from "../../../UIElements/Inputs/CalendarInput";
import ActNumberWithAutogenerateInput from "../../../UIElements/Inputs/ActNumberWithAutogenerateInput";
import RemainingPickerSidebar from "../../../UIElements/Sidebars/RemainingPickerSidebar";
import { DisplayErrors } from "../../../../Utils/ErrorHandler";

const NewWriteOff = () => {

    const ctx = useContext(EnumsContext);
    const isMobile = useIsMobile();
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef(null);

    const { createWriteOffAct, fetchWriteOffActDetails, fetchWriteOffActDocuments, fetchWriteOffActHistory, updateWriteOffAct } = useApi();

    const [actHistory, setActHistory] = useState(null);
    const [docArchive, setDocArchive] = useState(null);
    const [docArchiveTotalRecords, setDocArchiveTotalRecords] = useState(0);
    const [isEdit, setIsEdit] = useState(false);
    const [isFinalized, setIsFinalized] = useState(false);
    const [historyTotalRecords, setHistoryTotalRecords] = useState(0);
    const [tabShowed, setTabShowed] = useState(0);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [actData, setActData] = useState({
        division: null,
        act_number_autogenerate: true,
        act_number: '',
        act_date: new Date(),
        status: "Драфт",
        identified_products: [],
        unidentified_products: [],
    });
    const [docArchiveLazyState, setDocArchiveLazyState] = useState({
        first: 0,
        rows: 10,
        page: 1
    });
    const [historyLazyState, setHistoryLazyState] = useState({
        first: 0,
        rows: 10,
        page: 1
    });

    const enumTypes = useMemo(() => ["divisions"], []);

    useGetEnums(enumTypes);

    useEffect(() => {
        if (location.state !== null) {

            setIsEdit(true);

            const fetchData = async () => {

                const response = await fetchWriteOffActDetails(location.state.id);

                if (response.status === 200) {
                    console.log(response.data);

                    setIsFinalized(response.data.status !== "PENDING");
                    response.data.act_date = new Date(response.data.act_date);

                    setActData(response.data);
                }
            }

            fetchData();
        }
    }, [location.state, fetchWriteOffActDetails]);

    const formik = useFormik({
        initialValues: actData,
        enableReinitialize: true,
        validate: values => {
            const errors = {};

            if (!values.division) {
                errors.division = "Виберіть, будь ласка, точку продажу";
            }

            return errors;
        },
        onSubmit: async (values) => {

            setSubmitLoading(true);

            const payload = {
                ...values,
                act_date: formatShortDate(values.act_date)
            }

            const response = !isEdit ? await createWriteOffAct(payload) : await updateWriteOffAct(values.id, payload);

            setSubmitLoading(false);

            const successStatus = isEdit ? 200 : 201;

            if (response.status === successStatus) {
                const toastMessage = isEdit ? { severity: "success", summary: "Успіх редагування", detail: "Акт успішно відредаговано", life: 3000 } :
                    { severity: "success", summary: "Успіх створення", detail: "Акт успішно створено", life: 3000 }

                navigate("/writeoff", { state: { toast: toastMessage } });
            }
            else{
                DisplayErrors(response.error, toast);
            }
        }
    });

    const items = [
        {
            label: 'Деталі акту списання',
            icon: 'pi pi-book',
            command: () => {
                setTabShowed(0);
            },
        },
        ...(isEdit
            ? [
                {
                    label: 'Історія',
                    icon: 'pi pi-history',
                    command: () => {
                        setTabShowed(2);
                        getWriteOffActHistory(location.state.id, historyLazyState);
                    },
                },
                {
                    label: 'Документи',
                    icon: 'pi pi-save',
                    command: () => {
                        setTabShowed(3);
                        getDocumentsArchive(location.state.id, historyLazyState);
                    },
                },
            ]
            : []),
    ];

    const DeleteFieldHandler = (id, fields, name) => {

        const updatedFields = [...fields];

        const index = fields.findIndex(function (field) {
            return field.fe_id === id
        });

        updatedFields.splice(index, 1);
        formik.setFieldValue(name, updatedFields);
    };

    const AddProductHandler = () => {
        setVisible(true);
    }

    const getWriteOffActHistory = async (id, state) => {

        const response = await fetchWriteOffActHistory(id, state);

        console.log(response);

        if (response.status === 200) {
            setActHistory(response.data.results);
            setHistoryTotalRecords(response.data.count);
        }
        else {
            toast.current.show({ severity: "error", summary: "Помилка отримання історії", detail: "Помилка сервера " + response.status, life: 3000 });
        }
    }

    const onHistoryPage = (event) => {
        event.page = event.page + 1;
        getWriteOffActHistory(location.state.id, event);

        setHistoryLazyState(event);
    }

    const onDocArchivePage = (event) => {
        event.page = event.page + 1;
        getDocumentsArchive(location.state.id, event);

        setDocArchiveLazyState(event);
    }

    const getDocumentsArchive = async (id, lazyState) => {

        const response = await fetchWriteOffActDocuments(id, lazyState);

        if (response.status === 200) {
            setDocArchive(response.data.results);
            setDocArchiveTotalRecords(response.data.count);
        }
    }

    const toolbarLeftData = () => {

        let name = "Створення нового акту списання";

        if (isEdit) {
            name = "Редагування акту списання №" + formik.values.act_number;

            if (isFinalized) {
                name = "Інформація про акт списання №" + formik.values.act_number;
            }
        }

        return !isMobile ? <h3>{name}</h3> : null;
    }

    const contentToolbarRightTemplate = () => {
        return (<>
            <div className="flex flex-wrap gap-2">
                <Button label="+Додати товар" severity="success" className={commonStyle.addButton} type="button" onClick={AddProductHandler} />
            </div>
        </>);
    }

    const setTableHeader = (tablename) => {
        return (
            <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                <span className="text-xl text-900 font-bold">{tablename}</span>
            </div>
        );
    }

    const tableActionBodyTemplate = (rowData) => {

        var isId = Object.hasOwn(rowData, 'identifier');

        return (<div className="flex flex-wrap gap-2">
            {!isFinalized && <Button
                icon="pi pi-trash"
                type="button"
                className={`p-button-rounded p-button-danger ${commonStyle.deleteButton}`}
                tooltip="Видалити з таблиці"
                tooltipOptions={{ showOnDisabled: true, position: "top" }}
                onClick={() => DeleteFieldHandler(rowData.fe_id, isId ? formik.values.identified_products : formik.values.unidentified_products, isId ? "identified_products" : "unidentified_products")}
            />}
        </div>
        );
    };

    const confirmLeave = (event) => {

        if (formik.values !== actData) {

            confirmDialog({
                group: 'headless',
                header: 'Незбережені зміни',
                message: 'У вас є незбережені зміни. Ви впевнені, що хочете залишити цю сторінку?',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: "Так",
                rejectLabel: "Hi",
                defaultFocus: 'accept',
                accept: () => { navigate("/writeoff", { state: { pageState: location?.state?.pageState } }) }
            });

        }
        else {
            navigate("/writeoff", { state: { pageState: location?.state?.pageState } });
        }
    }

    const AddProductToTable = (product) => {
        const productToPass = {};

        productToPass.product = product.product_name;
        productToPass.remaining_id = product.id;

        if (!product.identifier) {
            productToPass.quantity = product.quantity;
            productToPass.note = product.note;
            productToPass.measurement_units = product.measurement_units;

            const products = [...formik.values.unidentified_products];

            products.push(productToPass);

            formik.setFieldValue("unidentified_products", products);
        }
        else {
            productToPass.identifier = product.identifier;
            const products = [...formik.values.identified_products];

            products.push(productToPass);

            formik.setFieldValue("identified_products", products)
        }
    }

    return (<form onSubmit={formik.handleSubmit}>
        <Toast ref={toast} />
        <CommonToolbar isMobile={isMobile} isFinalized={isFinalized} loading={submitLoading} onRetunButtonClick={(event) => confirmLeave(event)} leftContent={toolbarLeftData()} />
        <ConfirmDialog group="headless" />
        <TabMenu model={items} />
        {tabShowed === 0 && <div className="grid">
            <div className={`${commonStyle.leftMenu} col-3`}>
                <DropdownInput
                    formik={formik}
                    paramName={"division"}
                    context={ctx.enums.divisions.data}
                    onChange={(val) => formik.setFieldValue("division", val)}
                    label={"Точка продажу"}
                    isDisabled={isFinalized}
                    isMobile={isMobile}
                    mandatory />
                <CalendarInput formik={formik} paramName={"act_date"} label={"Дата акту"} isDisabled={isFinalized} isMobile={isMobile} />
                <ActNumberWithAutogenerateInput formik={formik} showAutogenerate={!isEdit} isDisabled={isFinalized} />
                <div className={commonStyle.formInput}>
                    <div className="p-inputgroup flex-column">
                        <FloatLabel>
                            <InputText value={formik.values.status === "FINALIZED" ? "Фіналізований" : "Драфт"}
                                disabled
                                className="w-full" />
                            <label>Статус</label>
                        </FloatLabel>
                    </div>
                </div>
            </div>
            <div className="col-9">
                {!isFinalized && <Toolbar className="mb-4" end={contentToolbarRightTemplate}></Toolbar>}
                {(formik.values.identified_products.length > 0) && <DataTable value={formik.values.identified_products} header={setTableHeader("Товари індивідуального обліку")}
                    tableStyle={{ minWidth: '50rem' }}>
                    <Column field="product" header="Продукт" style={{ width: '40%' }} />
                    <Column field="identifier" header="Ідентифікатор" style={{ width: '15%' }} />
                    <Column field="note" header="Примітка" style={{ width: '30%' }} />
                    <Column field="action" body={tableActionBodyTemplate} style={{ width: '15%' }} />
                </DataTable>}
                {(formik.values.unidentified_products.length > 0) && <DataTable value={formik.values.unidentified_products} header={setTableHeader("Товари поштучного обліку")}
                    tableStyle={{ minWidth: '50rem' }}>
                    <Column field="product" header="Продукт" style={{ width: '40%' }} />
                    <Column field="quantity" body={(rowData) => formatQuantityWithMeasurement(rowData.quantity, rowData.measurement_units)} header="Кількість" style={{ width: '15%' }} />
                    <Column field="note" header="Примітка" style={{ width: '30%' }} />
                    <Column field="action" body={tableActionBodyTemplate} style={{ width: '15%' }} />
                </DataTable>}
            </div>
        </div>
        }
        {tabShowed === 2 && <HistoryElement data={actHistory} lazyState={historyLazyState} totalRecords={historyTotalRecords} onHistoryPage={onHistoryPage} isAct={true} />}
        {tabShowed === 3 && <DocumentArchiveTable documents={docArchive} lazyState={docArchiveLazyState} onPage={onDocArchivePage} totalRecords={docArchiveTotalRecords} toast={toast} />}
        <RemainingPickerSidebar visible={visible} onHide={() => setVisible(false)} isModile={isMobile} toast={toast} formik={formik} ctx={ctx} onProductPick={(prod => AddProductToTable(prod))} divisionField="division" />
    </form>);
}

export default NewWriteOff;